import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useI18n } from "@/i18n";
import { hapticTap, openLink } from "@/tg/init";
import { getProgress, formatUAH } from "@/lib/getProgress";
import { DONATE_URL } from "@/lib/links";
import ImageWithFallback from "./ImageWithFallback";

interface FundraiserCardProps {
  id: string;
  kind: string;
  title: string;
  subtitle?: string | null | undefined;
  image?: string | null | undefined;
  uniform?: boolean;
}


export default function FundraiserCard({ id, kind, title, subtitle, image, uniform = false }: FundraiserCardProps) {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { percent, raised, target } = getProgress(id);

  function handleSupport() {
    hapticTap();
    openLink(DONATE_URL);
  }

  function handleDetails() {
    hapticTap();
    navigate(`/detail/${kind}/${id}`);
  }

  const safePercent = percent != null ? Math.min(100, Math.max(0, percent)) : 0;

  return (
    <motion.div
      whileTap={{ scale: 0.98 }}
      onClick={handleDetails}
      style={{
        borderRadius: "var(--r-card)",
        overflow: "hidden",
        marginBottom: 12,
        background: "var(--tint)",
        cursor: "pointer",
        WebkitTapHighlightColor: "transparent",
        padding: 8,
        ...(uniform ? { display: "flex", flexDirection: "column", height: "100%" } : {}),
      }}
    >
      {/* Image — 4:3 cover, inset, 4px rounded (matches site) */}
      <div style={{ width: "100%", aspectRatio: "4 / 3", overflow: "hidden", borderRadius: 8 }}>
        <ImageWithFallback
          src={image}
          alt=""
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      </div>

      <div style={{ padding: "8px 0 0", ...(uniform ? { display: "flex", flexDirection: "column", flex: 1 } : {}) }}>
        <h2
          className="line-clamp-2 font-head"
          style={{
            fontSize: 16,
            fontWeight: 600,
            lineHeight: 1.3,
            color: "var(--ink)",
            margin: "0 0 4px",
            overflow: "hidden",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
          }}
        >
          {title}
        </h2>

        {(subtitle || uniform) && (
          <p
            style={{
              fontSize: 14,
              color: "var(--muted)",
              lineHeight: 1.5,
              margin: "0 0 10px",
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
          >
            {subtitle ?? ""}
          </p>
        )}

        {/* Progress labels row */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 2,
            ...(uniform ? { marginTop: "auto" } : {}),
          }}
        >
          <span style={{ fontSize: 12, color: "var(--muted)" }}>{t("collected")}</span>
          <span style={{ fontSize: 12, color: "var(--muted)" }}>{t("goal")}</span>
        </div>

        {/* Amounts row */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            marginBottom: 8,
          }}
        >
          <span style={{ fontSize: 14, fontWeight: 700, color: "var(--ink)" }}>
            {raised != null ? formatUAH(raised) : "—"}
          </span>
          <span style={{ fontSize: 14, color: "var(--muted)" }}>
            {formatUAH(target)}
          </span>
        </div>

        {/* Progress bar with percent badge at fill end */}
        <div style={{ position: "relative", marginBottom: 12 }}>
          {/* Track */}
          <div
            style={{
              background: "var(--border)",
              borderRadius: 9999,
              height: 8,
              overflow: "visible",
              position: "relative",
            }}
          >
            {/* Fill */}
            <div
              style={{
                width: `${safePercent}%`,
                height: "100%",
                background: "var(--text)",
                borderRadius: 9999,
                transition: "width 0.4s ease",
                position: "relative",
              }}
            >
              {/* Percent badge at end of fill */}
              <span
                style={{
                  position: "absolute",
                  right: 0,
                  top: "50%",
                  transform: "translate(50%, -50%)",
                  background: "var(--ink)",
                  color: "#ffffff",
                  fontSize: 10,
                  fontWeight: 700,
                  lineHeight: 1,
                  padding: "2px 5px",
                  borderRadius: 99,
                  whiteSpace: "nowrap",
                  pointerEvents: "none",
                }}
              >
                {safePercent}%
              </span>
            </div>
          </div>
        </div>

        <button
          onClick={(e) => { e.stopPropagation(); handleSupport(); }}
          aria-label={`Support ${title}`}
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            padding: "14px 24px",
            borderRadius: "var(--r-pill)",
            background: "var(--brand-blue)",
            color: "#ffffff",
            fontFamily: '"e-Ukraine", system-ui, sans-serif',
            fontWeight: 500,
            fontSize: 16,
            lineHeight: 1,
            border: "none",
            cursor: "pointer",
            WebkitTapHighlightColor: "transparent",
            transition: "opacity 0.15s ease",
          }}
        >
          {t("support")}
          <ArrowUpRight size={20} strokeWidth={2.5} style={{ flexShrink: 0 }} />
        </button>
      </div>
    </motion.div>
  );
}
